import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Jurisdiction } from "@obg/core.config";
import { PlatformService } from "@obg/ngx.global.evo";
import { EMPTY, Observable, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";

import { AppError, AppErrorType } from "../app-error/app-error.model";
import { AuthTransactionResponseV2, VerificationRequestV2 } from "../auth-transaction/auth-transaction.model";
import { AuthTransactionService } from "../auth-transaction/auth-transaction.service";
import { GeolocationService } from "../geolocation/geolocation.service";
import { CoreState } from "../state";

import { LoginWithAuthenticationToken } from "./auth.model";
import { SessionAction } from "./session.action";
import { SessionClient } from "./session.client";
import { SessionConfigService } from "./session.config";
import {
	LoginRequest,
	LoginRequestOption,
	LoginSuccessfulResponse,
	LoginWithPersonalNumberRequest,
	LoginWithTokenRequest,
	LoginWithVerificationCodeRequest,
	SessionCustomEvent,
	SessionDomEventPayload,
	SessionLimitNextTimeLoginResponse,
	SessionResponse,
	TimeToLiveResponse,
	TwoFactorAuthLogin,
} from "./session.model";

@Injectable({
	providedIn: "root",
})
export class SessionService {
	constructor(
		private store: Store<CoreState>,
		private client: SessionClient,
		private platformService: PlatformService,
		private config: SessionConfigService,
		private authTransactionService: AuthTransactionService,
		private sessionAction: SessionAction,
		private geolocationService: GeolocationService
	) {}

	login(request: LoginRequest | TwoFactorAuthLogin): Observable<LoginSuccessfulResponse> {
		const loginData = {
			...request,
			username: request.username?.trim(),
		};
		return this.client.login(loginData).pipe(tap((response) => this.client.setSessionToken(response.sessionToken)));
	}

	loginWithVerificationCode(request: LoginWithVerificationCodeRequest): Observable<LoginSuccessfulResponse> {
		return this.client
			.loginWithVerificationCode(request)
			.pipe(tap((response) => this.client.setSessionToken(response.sessionToken)));
	}

	loginWithPersonalNumber(request: LoginWithPersonalNumberRequest): Observable<LoginSuccessfulResponse> {
		return this.client
			.loginWithPersonalNumber(request)
			.pipe(tap((response) => this.client.setSessionToken(response.sessionToken)));
	}

	loginWithToken(request: LoginWithTokenRequest): Observable<LoginSuccessfulResponse> {
		return this.client
			.loginWithToken(request)
			.pipe(tap((response) => this.client.setSessionToken(response.sessionToken)));
	}

	loginWithAuthenticationToken(request: LoginWithAuthenticationToken): Observable<LoginSuccessfulResponse> {
		return this.client.loginWithAuthenticationToken(request).pipe(
			tap((response) => {
				return this.client.setSessionToken(response.sessionToken);
			})
		);
	}

	logout(sessionToken?: string): Observable<object> {
		this.clearSessionToken();
		return this.client.logout(sessionToken);
	}

	clearSessionToken() {
		this.client.clearSessionToken();
	}

	verifySession(sessionToken: string | undefined): Observable<SessionResponse> {
		if (!sessionToken) {
			return throwError(
				() =>
					({
						type: AppErrorType.UnhandledError,
						data: "session-token-not-found",
					}) as AppError
			);
		}

		return this.client.getSession(sessionToken).pipe(
			// todo: remove this map once API is aligned
			map((response) => ({
				sessionToken,
				timeToLiveSeconds: response.timeToLiveSeconds,
			})),
			tap((response) => this.client.setSessionToken(response.sessionToken)),
			catchError((error: AppError) => {
				this.clearSessionToken();
				return throwError(() => error);
			})
		);
	}

	keepAlive(sessionToken: string): Observable<TimeToLiveResponse> {
		return this.client.keepAlive(sessionToken);
	}

	getSessionErrorResponse(sessionToken: string): Observable<SessionResponse> {
		return this.client.getSession(sessionToken);
	}

	getNextTimeLoginAfterSessionLimitReached(): Observable<SessionLimitNextTimeLoginResponse> {
		return this.client.getNextTimeLoginAfterSessionLimitReached();
	}

	sessionDomEvent(eventName: SessionCustomEvent, params?: Record<string, unknown>): void {
		if (!this.platformService.isBrowser) {
			return;
		}

		document.dispatchEvent(
			new CustomEvent<SessionDomEventPayload>(eventName, {
				bubbles: true,
				detail: {
					date: new Date(),
					...params,
				},
			})
		);
	}

	authenticationTransactionV2(loginRequest: LoginRequest): Observable<AuthTransactionResponseV2> {
		if (!loginRequest.username || !loginRequest.password) return EMPTY;

		const payload: VerificationRequestV2 = {
			iovationBlackBox: loginRequest.iovationBlackBox as string,
			fraudToken: loginRequest.fraudToken,
			authenticationType: "UsernamePassword",
			username: loginRequest.username,
			password: loginRequest.password,
		};
		return this.authTransactionService.initializeVerificationV2(payload).pipe(
			catchError((error: AppError) => {
				this.store.dispatch(this.sessionAction.loginFail(error));
				return EMPTY;
			})
		);
	}

	initBrandRedirectionVerification(
		authTransactionResponse: AuthTransactionResponseV2,
		loginOptions: LoginRequestOption | undefined,
		loginRequest: LoginRequest
	): LoginRequest | undefined {
		const isAuthenticationToken = authTransactionResponse.authenticationToken;
		const jurisdiction = authTransactionResponse.jurisdiction as Jurisdiction;
		const customerCountry = authTransactionResponse.customerCountry;

		if (isAuthenticationToken) {
			if (loginOptions?.disallowRedirect) {
				this.store.dispatch(this.sessionAction.authenticationTransaction(isAuthenticationToken));
				return loginRequest;
			}

			const isRedirectToBrandDomain = this.isRedirectToBrandDomain(
				jurisdiction,
				isAuthenticationToken,
				customerCountry
			);

			return isRedirectToBrandDomain ? undefined : loginRequest;
		}

		return loginRequest;
	}

	isUserAllowedToLogin(customerCountry: string, loginOptions: LoginRequestOption): boolean {
		if (!loginOptions?.disallowLogin) return true;

		const isLoginAllowedForCountries = this.config.isLoginAllowedForCountries();
		const loginAllowedCountries = this.config.getLoginAllowedCountries();
		const loginDisallowedCountries = this.config.getLoginDisallowedCountries();

		if (
			loginDisallowedCountries.includes(customerCountry) ||
			(isLoginAllowedForCountries && !loginAllowedCountries.includes(customerCountry))
		) {
			this.store.dispatch(
				this.sessionAction.loginFail({
					code: "DISALLOWED_ERROR_CODE",
					type: AppErrorType.Unauthorized,
					data: {},
				})
			);

			this.store.dispatch(this.sessionAction.loginCountryRestriction(customerCountry));

			return false;
		}

		return true;
	}

	private isRedirectToBrandDomain(
		jurisdiction: Jurisdiction,
		authenticationToken: string,
		customerCountry: string
	): boolean {
		if (this.config.getRedirectionDisabledCustomerCountries().includes(customerCountry)) {
			return false;
		}

		const targetDomainByJurisdiction = this.config.getRedirectionDomainByJurisdiction()[jurisdiction];
		const targetDomainByCustomerCountry = this.getCountryDomain(customerCountry);

		const targetDomain = targetDomainByCustomerCountry ?? targetDomainByJurisdiction;

		if (!targetDomain) {
			return false;
		}

		const redirectUrlTemplate =
			this.config.getRedirectTemplate()[jurisdiction] ?? this.config.getDefaultRedirectionTemplate();

		const encodedDomain = encodeURIComponent(targetDomain);
		const encodedAuthenticationToken = encodeURIComponent(authenticationToken);

		const redirectUrl = new URL(redirectUrlTemplate.replace("{{domain}}", encodedDomain));

		redirectUrl.searchParams.append("authenticationToken", encodedAuthenticationToken);
		this.store.dispatch(this.sessionAction.loginBrandRedirection());
		window.location.href = redirectUrl.href;

		return true;
	}

	private getCountryDomain(customerCountry: string): string | undefined {
		return this.config.getRedirectionDomainByCustomerCountry().find((redirectionDomainByCustomerCountry) => {
			return (
				(redirectionDomainByCustomerCountry.areCountriesForbidden &&
					redirectionDomainByCustomerCountry.customerCountries?.includes(customerCountry)) ||
				(!redirectionDomainByCustomerCountry.areCountriesForbidden &&
					!redirectionDomainByCustomerCountry.customerCountries?.includes(customerCountry))
			);
		})?.redirectionDomain;
	}

	checkIfUserIsFraudulentDuringSession$(customerId: string): Observable<void> {
		return this.geolocationService.checkIfTheUserIsFraud$("LOGIN", customerId).pipe(
			map((isUserFraud) => {
				if (!isUserFraud.isFraud) return;
				return this.store.dispatch(
					this.sessionAction.logout({
						shouldSkipNotification: true,
						isUserFraud: isUserFraud.isFraud,
					})
				);
			}),
			catchError((error) => {
				this.store.dispatch(this.sessionAction.loginFail(error));
				return EMPTY;
			})
		);
	}
}
